/*!
 * Boreal Gaming — Quick Add to Cart  v1.0.0
 * ---------------------------------------------------------------
 * Handles every "Add to cart" button with [data-card-atc] (collection
 * grid cards, search results, etc.) and replaces the legacy
 * jQuery/Toastify flow.
 *
 *  - One round trip: POST /cart/add.js with Shopify's bundled section
 *    rendering, so the header count + mini-cart come back with the add.
 *  - Button states: loading spinner -> "Added ✓" -> restored (no layout jump).
 *  - Cart notification with product image, qty, price, View cart / Checkout.
 *    Desktop: top-right under the sticky nav. Mobile: bottom sheet.
 *    Auto-dismiss (pauses on hover/focus), Esc / × to close.
 *  - Screen-reader announcement via a polite live region.
 *  - Real Shopify error messages (sold out, limit reached, offline, 429).
 *  - Back-compat: window.addToCart() and window.updateItemCount() are
 *    redirected here so older callers (decklist, product form) also
 *    refresh the header correctly.
 *
 * Optional config (set BEFORE this file loads):
 *   window.BGCQuickAddConfig = { notifyDuration: 6000, headerSection: 'header-3' };
 */
(function () {
  'use strict';

  if (window.BGCCart && window.BGCCart.__v) return;

  // Stops the old quick-add block in custom.js from binding if it loads later.
  window.__bgcCardQuickAdd = true;

  var CFG = Object.assign(
    {
      selector: '[data-card-atc]',
      headerSection: 'header-3', // id of the static header section (shopify-section-header-3)
      notifyDuration: 5000, // ms; 0 = stay until closed
      addedResetDelay: 1800, // ms the button shows "Added ✓"
      errorResetDelay: 2600,
      mobileMax: 767, // px; at or below = bottom-sheet notification
      text: {
        adding: 'Adding…',
        added: 'Added',
        addedTitle: 'Added to cart',
        errorTitle: "Couldn't add to cart",
        viewCart: 'View cart',
        checkout: 'Checkout',
        close: 'Close',
        qty: 'Qty',
        soldOut: 'Sold out',
        limit: 'Max in cart',
        retry: 'Try again',
        offline: 'You appear to be offline. Check your connection and try again.',
        rateLimited: 'Too many requests. Please wait a moment and try again.',
        generic: 'Something went wrong. Please try again.'
      }
    },
    window.BGCQuickAddConfig || {}
  );
  var T = CFG.text;

  var routes = window.routes || {};
  var URL_ADD = (routes.cartAddUrl || '/cart/add') + '.js';
  var URL_CART = routes.cartUrl || '/cart';
  var URL_CHECKOUT = ((routes.rootUrl || '/').replace(/\/$/, '')) + '/checkout';

  /* ---------------------------------------------------------------- utils */

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
  }

  function formatMoney(cents) {
    var fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    cents = Number(cents) || 0;
    function num(n, dec, thou, decSep) {
      var parts = (n / 100).toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou);
      return parts.join(decSep);
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount_no_decimals': return num(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return num(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return num(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return num(cents, 2, "'", '.');
        default: return num(cents, 2, ',', '.');
      }
    });
  }

  function sizedImage(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function bump(el) {
    el.classList.remove('bgc-bump');
    void el.offsetWidth; // restart animation
    el.classList.add('bgc-bump');
  }

  function isMobile() {
    return window.matchMedia('(max-width: ' + CFG.mobileMax + 'px)').matches;
  }

  /* ------------------------------------------------ header / mini-cart sync */

  // Applies the rendered header section (from bundled section rendering)
  // to the live header: cart count + mini-cart contents. Returns item count.
  function applySections(sections) {
    var html = sections && sections[CFG.headerSection];
    if (!html) return null;

    var doc = new DOMParser().parseFromString(html, 'text/html');
    var count = null;

    var newMini = doc.querySelector('.mini-cart');
    var mini = document.querySelector('.mini-cart');
    if (newMini && mini) {
      var list = mini.querySelector('.mini-cart__line-item-list');
      var scroll = list ? list.scrollTop : 0;
      mini.innerHTML = newMini.innerHTML;
      if (newMini.hasAttribute('data-item-count')) {
        mini.setAttribute('data-item-count', newMini.getAttribute('data-item-count'));
        count = parseInt(newMini.getAttribute('data-item-count'), 10);
      }
      var newList = mini.querySelector('.mini-cart__line-item-list');
      if (newList) newList.scrollTop = scroll;
    }

    var newCounts = doc.querySelectorAll('.header__cart-count');
    document.querySelectorAll('.header__cart-count').forEach(function (el, i) {
      var src = newCounts[i] || newCounts[0];
      if (!src) return;
      var txt = src.textContent.trim();
      if (count === null) count = parseInt(txt, 10);
      if (el.textContent.trim() !== txt) {
        el.textContent = txt;
        bump(el);
      }
    });

    if (count !== null && !isNaN(count) && window.theme) window.theme.cartCount = count;
    emit('bgc:cart:updated', { count: count });
    return count;
  }

  function refresh() {
    var url = window.location.pathname + '?sections=' + encodeURIComponent(CFG.headerSection);
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(applySections)
      .catch(function () { return null; });
  }

  /* ------------------------------------------------------------------- add */

  // items: [{ id: variantId, quantity: n, properties?: {} }]
  function add(items) {
    var body = {
      items: items,
      sections: CFG.headerSection,
      sections_url: window.location.pathname
    };
    return fetch(URL_ADD, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res
          .json()
          .catch(function () { return {}; })
          .then(function (data) {
            if (!res.ok) {
              var err = new Error(data.description || data.message || T.generic);
              err.status = res.status;
              err.data = data;
              throw err;
            }
            return data;
          });
      })
      .then(function (data) {
        var added = data.items || [data];
        var count = applySections(data.sections);
        var done = count === null ? refresh() : Promise.resolve(count);
        return done.then(function (c) {
          var result = { items: added, count: c };
          emit('bgc:cart:added', result);
          return result;
        });
      });
  }

  function describeError(err, ctx) {
    ctx = ctx || {};
    if (!navigator.onLine) return { msg: T.offline, label: T.retry };
    if (err && err.status === 429) return { msg: T.rateLimited, label: T.retry };
    var desc = (err && err.message) || T.generic;
    if (err && err.status === 422) {
      if (/sold out/i.test(desc)) return { msg: desc, label: T.soldOut };
      // Shopify: "You can't add more X to the cart." -> friendlier copy when we know the stock.
      if (ctx.max && ctx.title && /(can.?t|cannot) add more|maximum/i.test(desc)) {
        return {
          msg: 'All ' + ctx.max + ' available of ' + ctx.title + ' are already in your cart.',
          label: T.limit
        };
      }
      return { msg: desc, label: T.limit };
    }
    return { msg: desc, label: T.retry };
  }

  /* --------------------------------------------------------- button states */

  var CHECK_SVG =
    '<svg class="bgc-atc-check" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
    '<path d="M4 10.5l4 4 8-9" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function setState(btn, state, label) {
    if (!btn) return;
    if (btn.__bgcLabel === undefined) btn.__bgcLabel = btn.innerHTML;
    clearTimeout(btn.__bgcTimer);
    btn.classList.remove('is-loading', 'is-added', 'is-error');

    if (state === 'idle') {
      btn.innerHTML = btn.__bgcLabel;
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
      btn.style.minWidth = '';
      btn.__bgcLabel = undefined;
      return;
    }

    if (!btn.style.minWidth) btn.style.minWidth = btn.offsetWidth + 'px'; // no layout jump
    btn.classList.add('is-' + state);

    if (state === 'loading') {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      btn.innerHTML =
        '<span class="bgc-atc-label"><span class="bgc-atc-spinner" aria-hidden="true"></span>' +
        '<span class="bgc-sr">' + T.adding + '</span></span>';
      return;
    }

    btn.removeAttribute('aria-busy');
    if (state === 'added') {
      btn.innerHTML = '<span class="bgc-atc-label">' + CHECK_SVG + '<span>' + T.added + '</span></span>';
    } else {
      var span = document.createElement('span');
      span.className = 'bgc-atc-label';
      span.textContent = label || T.retry;
      btn.innerHTML = '';
      btn.appendChild(span);
    }
    btn.__bgcTimer = setTimeout(function () {
      setState(btn, 'idle');
    }, state === 'added' ? CFG.addedResetDelay : CFG.errorResetDelay);
  }

  /* ----------------------------------------------------------- notification */

  var notice = (function () {
    var root, panel, titleEl, iconEl, img, nameEl, metaEl, msgEl, viewBtn, checkoutBtn, live, bar;
    var timer = null, remaining = 0, startedAt = 0, paused = false;

    function build() {
      if (root) return;
      root = document.createElement('div');
      root.className = 'bgc-notice';
      root.setAttribute('role', 'region');
      root.setAttribute('aria-label', 'Cart notification');
      root.hidden = true;
      root.innerHTML =
        '<div class="bgc-notice__panel">' +
          '<div class="bgc-notice__head">' +
            '<span class="bgc-notice__icon" aria-hidden="true"></span>' +
            '<p class="bgc-notice__title"></p>' +
            '<button type="button" class="bgc-notice__close" aria-label="' + T.close + '">' +
              '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
            '</button>' +
          '</div>' +
          '<div class="bgc-notice__item">' +
            '<img class="bgc-notice__img" alt="" width="64" height="64" loading="eager">' +
            '<div class="bgc-notice__text">' +
              '<p class="bgc-notice__name"></p>' +
              '<p class="bgc-notice__meta"></p>' +
            '</div>' +
          '</div>' +
          '<p class="bgc-notice__msg"></p>' +
          '<div class="bgc-notice__actions">' +
            '<a class="bgc-notice__btn bgc-notice__btn--ghost" href="' + URL_CART + '"></a>' +
            '<a class="bgc-notice__btn bgc-notice__btn--primary" href="' + URL_CHECKOUT + '">' + T.checkout + '</a>' +
          '</div>' +
          '<span class="bgc-notice__bar" aria-hidden="true"></span>' +
        '</div>';

      live = document.createElement('div');
      live.className = 'bgc-sr';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');

      document.body.appendChild(root);
      document.body.appendChild(live);

      panel = root.querySelector('.bgc-notice__panel');
      titleEl = root.querySelector('.bgc-notice__title');
      iconEl = root.querySelector('.bgc-notice__icon');
      img = root.querySelector('.bgc-notice__img');
      nameEl = root.querySelector('.bgc-notice__name');
      metaEl = root.querySelector('.bgc-notice__meta');
      msgEl = root.querySelector('.bgc-notice__msg');
      viewBtn = root.querySelector('.bgc-notice__btn--ghost');
      checkoutBtn = root.querySelector('.bgc-notice__btn--primary');
      bar = root.querySelector('.bgc-notice__bar');

      root.querySelector('.bgc-notice__close').addEventListener('click', hide);
      root.addEventListener('mouseenter', pause);
      root.addEventListener('mouseleave', resume);
      root.addEventListener('focusin', pause);
      root.addEventListener('focusout', function (e) {
        if (!root.contains(e.relatedTarget)) resume();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !root.hidden) hide();
      });
      window.addEventListener('resize', position, { passive: true });
    }

    function position() {
      if (!root || root.hidden) return;
      if (isMobile()) {
        root.style.top = '';
        return;
      }
      var sticky = document.querySelector('.shopify-section__header');
      var bottom = sticky ? Math.max(0, sticky.getBoundingClientRect().bottom) : 0;
      root.style.top = Math.round(bottom + 12) + 'px';
    }

    function startTimer(ms) {
      clearTimeout(timer);
      if (!CFG.notifyDuration) return;
      remaining = ms;
      startedAt = Date.now();
      timer = setTimeout(hide, ms);
    }
    function pause() {
      if (paused || !CFG.notifyDuration) return;
      paused = true;
      clearTimeout(timer);
      remaining -= Date.now() - startedAt;
      root.classList.add('is-paused');
    }
    function resume() {
      if (!paused) return;
      paused = false;
      root.classList.remove('is-paused');
      startTimer(Math.max(remaining, 1500));
    }

    function show(kind) {
      build();
      paused = false;
      root.classList.remove('is-paused', 'is-success', 'is-error');
      root.classList.add('is-' + kind);
      root.classList.toggle('is-mobile', isMobile());
      root.hidden = false;
      position();
      // restart entrance + progress animations
      root.classList.remove('is-visible');
      bar.style.animation = 'none';
      void root.offsetWidth;
      bar.style.animation = '';
      bar.style.animationDuration = CFG.notifyDuration ? CFG.notifyDuration + 'ms' : '0s';
      root.classList.add('is-visible');
      startTimer(CFG.notifyDuration);
    }

    function hide() {
      if (!root || root.hidden) return;
      clearTimeout(timer);
      root.classList.remove('is-visible');
      setTimeout(function () {
        if (!root.classList.contains('is-visible')) root.hidden = true;
      }, 220);
    }

    function announce(text) {
      live.textContent = '';
      setTimeout(function () { live.textContent = text; }, 50);
    }

    function success(item, count, sourceBtn) {
      build();
      item = item || {};
      var title = item.product_title || item.title || '';
      var variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      iconEl.innerHTML = CHECK_SVG;
      titleEl.textContent = T.addedTitle;
      nameEl.textContent = title + (variant ? ' — ' + variant : '');
      var qty = item.quantity || 1;
      var price = item.final_price != null ? item.final_price : item.price;
      metaEl.textContent = T.qty + ' ' + qty + (price != null ? ' · ' + formatMoney(price) : '');
      // Prefer the card's already-loaded image (instant, from cache); fall back to the cart image.
      var cardImg = sourceBtn && sourceBtn.closest('.productCard__card') &&
        sourceBtn.closest('.productCard__card').querySelector('img');
      var cardSrc = cardImg && cardImg.complete && (cardImg.currentSrc || cardImg.src);
      if (cardSrc || item.image) {
        img.src = cardSrc || sizedImage(item.image, 160);
        img.hidden = false;
      } else {
        img.hidden = true;
      }
      root.querySelector('.bgc-notice__item').hidden = false;
      msgEl.hidden = true;
      viewBtn.textContent = T.viewCart + (count ? ' (' + count + ')' : '');
      checkoutBtn.hidden = false;
      show('success');
      announce(T.addedTitle + ': ' + title + '. ' + (count ? 'Cart has ' + count + ' item' + (count === 1 ? '' : 's') + '.' : ''));
    }

    function error(message) {
      build();
      iconEl.textContent = '!';
      titleEl.textContent = T.errorTitle;
      root.querySelector('.bgc-notice__item').hidden = true;
      msgEl.textContent = message;
      msgEl.hidden = false;
      viewBtn.textContent = T.viewCart;
      checkoutBtn.hidden = true;
      show('error');
      announce(T.errorTitle + '. ' + message);
    }

    return { success: success, error: error, hide: hide };
  })();

  /* ------------------------------------------------------- click delegation */

  // Opening the header mini-cart supersedes the notification (avoid stacking two popovers).
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-action="toggle-mini-cart"]')) notice.hide();
  });

  // Capture phase so this runs before (and instead of) any legacy handler.
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest ? e.target.closest(CFG.selector) : null;
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;

      var id = parseInt(btn.getAttribute('data-variant-id'), 10);
      if (!id) return;
      var qty = parseInt(btn.getAttribute('data-quantity') || '1', 10) || 1;
      var ctx = { max: btn.getAttribute('data-max'), title: btn.getAttribute('data-title') };

      setState(btn, 'loading');
      add([{ id: id, quantity: qty }])
        .then(function (r) {
          setState(btn, 'added');
          notice.success(r.items[0], r.count, btn);
        })
        .catch(function (err) {
          var d = describeError(err, ctx);
          setState(btn, 'error', d.label);
          notice.error(d.msg);
        });
    },
    true
  );

  /* ------------------------------------------------------------ back-compat */

  function asDeferred(promise) {
    if (window.jQuery && jQuery.Deferred) {
      var d = jQuery.Deferred();
      promise.then(d.resolve, d.reject);
      return d.promise();
    }
    return promise;
  }

  // Legacy signature from application.js: addToCart(variantId, title, maxStock, qty, variantTitle)
  window.addToCart = function (item, itemTitle, maxStock, qty, variantTitle) {
    var p = add([{ id: parseInt(item, 10), quantity: parseInt(qty, 10) || 1 }]).then(
      function (r) {
        notice.success(r.items[0], r.count);
        return r;
      },
      function (err) {
        notice.error(describeError(err, { max: maxStock, title: itemTitle }).msg);
        throw err;
      }
    );
    return asDeferred(p);
  };

  // theme.js product form and the decklist importer call this after adding.
  window.updateItemCount = function () {
    return refresh();
  };

  window.BGCCart = {
    __v: '1.0.0',
    config: CFG,
    add: add,
    refresh: refresh,
    notify: notice,
    formatMoney: formatMoney
  };
})();
